import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, User, Phone, CheckCircle, XCircle, Loader2, LogOut, Activity, MessageSquare, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statsRes, apptRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/api/admin/appointments${filter !== 'all' ? `?status=${filter}` : ''}`, { headers: authHeaders })
      ]);
      setStats(statsRes.data);
      setAppointments(apptRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setToken('');
        sessionStorage.removeItem('admin_token');
      }
    }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/api/admin/login`, { password });
      setToken(res.data.token);
      sessionStorage.setItem('admin_token', res.data.token);
    } catch {
      setLoginError('Invalid password');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/api/admin/appointments/${id}`, { status }, { headers: authHeaders });
      fetchData();
    } catch (err) {
      // Error handled silently
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    try {
      await axios.delete(`${API}/api/admin/appointments/${id}`, { headers: authHeaders });
      fetchData();
    } catch (err) {
      // Error handled silently
    }
  };

  const logout = () => {
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4" data-testid="admin-login">
        <div className="w-full max-w-sm">
          <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
            <h1 className="font-outfit text-2xl text-white mb-1">Admin Dashboard</h1>
            <p className="text-sm text-white/40 font-manrope mb-6">Divyadeep Clinic Management</p>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-manrope text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D1A760]/50 mb-3"
                data-testid="admin-password-input"
              />
              {loginError && <p className="text-red-400 text-xs font-manrope mb-3" data-testid="admin-login-error">{loginError}</p>}
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D1A760] to-[#B89255] text-white font-manrope font-medium text-sm hover:shadow-[0_4px_16px_rgba(209,167,96,0.3)] transition-all"
                data-testid="admin-login-btn"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] p-4 md:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl text-white">Dashboard</h1>
          <p className="text-sm text-white/40 font-manrope">Divyadeep Clinic — Appointment Management</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all text-sm font-manrope" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {[
            { label: 'Total', value: stats.total_appointments, icon: Activity, color: 'text-white' },
            { label: 'Confirmed', value: stats.confirmed, icon: CheckCircle, color: 'text-emerald-400' },
            { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-blue-400' },
            { label: 'Cancelled', value: stats.cancelled, icon: XCircle, color: 'text-red-400' },
            { label: 'Today', value: stats.today_appointments, icon: Calendar, color: 'text-[#D1A760]' },
            { label: 'Contacts', value: stats.total_contacts, icon: MessageSquare, color: 'text-purple-400' },
          ].map(stat => (
            <div key={stat.label} className="bg-white/5 rounded-2xl p-4 border border-white/5">
              <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
              <p className={`font-outfit text-2xl font-semibold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-white/40 font-manrope">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2 flex-wrap">
          {['all', 'confirmed', 'completed', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl text-xs font-manrope font-medium transition-all ${
                filter === f
                  ? 'bg-[#D1A760] text-white'
                  : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'
              }`}
              data-testid={`admin-filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Appointments */}
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D1A760] animate-spin" />
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/30 font-manrope">No appointments found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map(appt => (
              <div key={appt.id} className="bg-white/5 rounded-2xl p-4 md:p-5 border border-white/5 hover:border-white/10 transition-all" data-testid={`appointment-card-${appt.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-[#D1A760] flex-shrink-0" />
                      <p className="font-manrope font-medium text-white text-sm truncate">{appt.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-manrope font-medium ${
                        appt.status === 'confirmed' ? 'bg-emerald-400/10 text-emerald-400' :
                        appt.status === 'completed' ? 'bg-blue-400/10 text-blue-400' :
                        'bg-red-400/10 text-red-400'
                      }`}>
                        {appt.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40 font-manrope">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{appt.phone}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{appt.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{appt.time_slot}</span>
                      <span className="text-[#D1A760]">{appt.service}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {appt.status !== 'completed' && (
                      <button onClick={() => updateStatus(appt.id, 'completed')} className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-[11px] font-manrope hover:bg-blue-500/20 transition-all" data-testid={`complete-btn-${appt.id}`}>
                        Complete
                      </button>
                    )}
                    {appt.status !== 'cancelled' && (
                      <button onClick={() => updateStatus(appt.id, 'cancelled')} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[11px] font-manrope hover:bg-red-500/20 transition-all" data-testid={`cancel-btn-${appt.id}`}>
                        Cancel
                      </button>
                    )}
                    <button onClick={() => deleteAppointment(appt.id)} className="p-1.5 rounded-lg bg-white/5 text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all" data-testid={`delete-btn-${appt.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
